import { useEffect } from 'react'
import { useAppDispatch, useAppSelector } from '~publish/legacy/store/index'
import { selectUserId } from '~publish/legacy/user/selectors'
import { UploadType, useUploader } from '@buffer-mono/uploader'
import { bindUploaderToReduxSlice } from '../state/bindUploaderToReduxSlice'
import BufferUploadsUppyPlugin from '../uppy/BufferUploadsUppyPlugin'
import GenerateThumbnailsUppyPlugin from '../uppy/GenerateThumbnailsUppyPlugin'
import { useOrganizationId } from '~publish/contexts/AccountContext'

export default function usePublishUploader(
  props: Omit<Parameters<typeof useUploader>[0], 'organizationId' | 'userId'>,
): ReturnType<typeof useUploader> {
  const dispatch = useAppDispatch()
  const userId = useAppSelector(selectUserId)
  // Using Context instead of Redux for organizationId since Context data
  // is available earlier, preventing tracking violations during extension
  // idea creation. Empty string default is safe as no uploads occur earlier
  // than the extension flow.
  const organizationId = useOrganizationId() || ''

  const uploader = useUploader({
    organizationId,
    userId,
    ...props,
    id: props.id || UploadType.postAsset,
  })

  useEffect(() => {
    bindUploaderToReduxSlice(uploader, { dispatch })
  }, [uploader, dispatch])

  useEffect(() => {
    uploader.uppyInstance
      .use(BufferUploadsUppyPlugin, {
        onComplete: (files) => {
          uploader.emit('upload-finished', files)
        },
        onError: (file, error) => {
          uploader.emit('upload-failed', file, error)
        },
      })
      .use(GenerateThumbnailsUppyPlugin, {
        onError: (file, error) => {
          uploader.emit('upload-failed', file, error)
        },
      })
  }, [uploader])

  return uploader
}
